// Audio recording widget.
//
// Recordings are streamed to the server chunk by chunk while they are still
// being recorded, so there is no length limit and an interrupted session keeps
// everything up to the last chunk. The hidden form input ends up holding a
// /api/files/{id}/{name} URL.
//
// Older answers hold a `data:audio/...;base64,...` URL instead. Both are valid
// values for an <audio src>, so past recordings keep playing untouched and no
// migration is needed.

export type AudioRecorderState =
  | 'idle' // nothing recorded yet
  | 'requesting' // waiting on the microphone permission prompt
  | 'recording'
  | 'finishing' // recorder stopped, last chunks still uploading
  | 'ready' // saved, safe to submit
  | 'error'

/** How much audio each chunk covers. Smaller means less to re-send after a
 * network blip and a smoother progress indicator; larger means fewer requests. */
const CHUNK_MS = 4000

const MAX_CHUNK_ATTEMPTS = 4

/** Ask for mono, speech-quality Opus. Pinning this is what makes elapsed time
 * and file size predictable instead of varying by browser and room noise. */
const AUDIO_BITS_PER_SECOND = 32000

/** Preferred container/codec in descending order. Safari only supports mp4. */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus'
]

export function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return '' // let the browser choose
}

export function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  )
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`
}

/**
 * Streams chunks to the server in order, retrying transient failures.
 *
 * Chunk POSTs are idempotent on (upload, seq) server side, so a retry after a
 * timeout can never duplicate audio.
 */
class ChunkedUpload {
  private uploadId: number | null = null
  private starting: Promise<number> | null = null
  private seq = 0
  /** Serializes chunk POSTs so they land in order. */
  private chain: Promise<void> = Promise.resolve()
  private failure: Error | null = null

  /** Bytes confirmed stored server side. */
  uploadedBytes = 0
  /** Bytes handed to us by the recorder. */
  totalBytes = 0

  constructor(
    private name: string,
    private type: string,
    private onProgress: () => void
  ) {}

  private async ensureStarted(): Promise<number> {
    if (this.uploadId !== null) return this.uploadId
    if (!this.starting) {
      this.starting = (async () => {
        const resp = await fetch('/api/uploads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: this.name, type: this.type })
        })
        if (!resp.ok) throw new Error(`Could not start upload (${resp.status})`)
        const body = await resp.json()
        this.uploadId = body.upload_id as number
        return this.uploadId
      })()
    }
    return this.starting
  }

  /** Queues a chunk. Returns immediately; failures surface via `error`. */
  append(blob: Blob) {
    if (blob.size === 0) return
    this.totalBytes += blob.size
    const seq = this.seq++
    this.chain = this.chain.then(async () => {
      if (this.failure) return // stop trying once the upload is broken
      try {
        await this.sendChunk(seq, blob)
        this.uploadedBytes += blob.size
        this.onProgress()
      } catch (err) {
        this.failure = err instanceof Error ? err : new Error(String(err))
        this.onProgress()
      }
    })
  }

  private async sendChunk(seq: number, blob: Blob) {
    const uploadId = await this.ensureStarted()
    let lastError: unknown
    for (let attempt = 0; attempt < MAX_CHUNK_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        // back off: 500ms, 1s, 2s
        await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)))
      }
      try {
        const resp = await fetch(`/api/uploads/${uploadId}/chunks/${seq}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: blob
        })
        if (resp.ok) return
        // 4xx other than 408/429 will not succeed on retry
        if (resp.status < 500 && resp.status !== 408 && resp.status !== 429) {
          throw new Error(`Upload rejected (${resp.status})`)
        }
        lastError = new Error(`Upload failed (${resp.status})`)
      } catch (err) {
        lastError = err
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Upload failed')
  }

  /** Waits for queued chunks, then assembles the file. Returns its URL. */
  async finalize(): Promise<string> {
    await this.chain
    if (this.failure) throw this.failure
    if (this.uploadId === null) throw new Error('Nothing was recorded')

    const resp = await fetch(`/api/uploads/${this.uploadId}/finalize`, { method: 'POST' })
    if (!resp.ok) throw new Error(`Could not save the recording (${resp.status})`)
    return (await resp.json()).file_url as string
  }

  get error(): Error | null {
    return this.failure
  }
}

interface Elements {
  root: HTMLElement
  button: HTMLButtonElement
  indicator: HTMLElement
  elapsed: HTMLElement
  status: HTMLElement
  progressWrap: HTMLElement
  progressBar: HTMLElement
  player: HTMLAudioElement
  input: HTMLInputElement
  rescue: HTMLAnchorElement
}

/** Drives one recorder widget. All DOM lookups are scoped to its own root so
 * multiple recorders on one page cannot interfere with each other. */
export class AudioRecorderController {
  state: AudioRecorderState = 'idle'
  private el: Elements
  private name: string
  private stream: MediaStream | null = null
  private recorder: MediaRecorder | null = null
  private upload: ChunkedUpload | null = null
  private chunks: Blob[] = []
  private mimeType = ''
  private startedAt = 0
  private tick: ReturnType<typeof setInterval> | null = null
  /** Resolves once the recording is saved (or has failed). */
  private settled: Promise<void> = Promise.resolve()

  constructor(root: HTMLElement) {
    this.el = {
      root,
      button: root.querySelector('.recordButton')!,
      indicator: root.querySelector('.audImg')!,
      elapsed: root.querySelector('.audio-elapsed')!,
      status: root.querySelector('.audio-status')!,
      progressWrap: root.querySelector('.audio-progress')!,
      progressBar: root.querySelector('.audio-progress-bar')!,
      player: root.querySelector('.audio-player')!,
      input: root.querySelector('.audioInput')!,
      rescue: root.querySelector('.audio-rescue')!
    }
    this.name = root.getAttribute('data-name') || ''

    if (!isRecordingSupported()) {
      this.el.button.disabled = true
      this.setStatus(
        'Audio recording is not supported by this browser. Please try Chrome, Edge, Firefox, or Safari.',
        'warn'
      )
      return
    }

    this.el.button.addEventListener('click', (e) => {
      e.preventDefault()
      void this.toggle()
    })

    // An existing answer means there is already a saved recording.
    if (this.el.input.value) this.state = 'ready'
  }

  /** True while it would be unsafe to submit the form. */
  get busy(): boolean {
    return this.state === 'requesting' || this.state === 'recording' || this.state === 'finishing'
  }

  /** Resolves when this recorder is no longer busy. */
  async whenSettled(): Promise<void> {
    await this.settled
  }

  private emitState() {
    this.el.root.setAttribute('data-state', this.state)
    document.dispatchEvent(
      new CustomEvent('hopekit:audio-state', {
        detail: { name: this.name, state: this.state, busy: this.busy }
      })
    )
  }

  private setStatus(message: string, tone: 'info' | 'good' | 'warn' = 'info') {
    const el = this.el.status
    el.textContent = message
    el.classList.remove('hidden')
    el.classList.remove(
      'bg-neutral',
      'border-neutral',
      'text-black',
      'bg-accent-primary',
      'border-accent-primary',
      'text-white',
      'bg-green-100',
      'border-green-600',
      'text-green-900'
    )
    if (tone === 'warn') {
      el.classList.add('bg-accent-primary', 'border-accent-primary', 'text-white')
    } else if (tone === 'good') {
      el.classList.add('bg-green-100', 'border-green-600', 'text-green-900')
    } else {
      el.classList.add('bg-neutral', 'border-neutral', 'text-black')
    }
  }

  private setState(state: AudioRecorderState) {
    this.state = state
    this.emitState()
  }

  private async toggle() {
    if (this.state === 'recording') {
      await this.stop()
      return
    }
    if (this.busy) return // ignore clicks while starting up or finishing
    await this.start()
  }

  private async start() {
    // Claim the settled promise before anything can await it, so a form that
    // submits while we are still starting up waits for us rather than racing.
    this.settled = new Promise<void>((resolve) => {
      this.resolveSettled = resolve
    })
    this.setState('requesting')
    this.el.button.disabled = true
    this.setStatus('Waiting for microphone permission…')

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    } catch (err) {
      // Permission denied, no microphone, or the device is in use elsewhere.
      this.el.button.disabled = false
      this.setState('error')
      this.setStatus(
        'We could not access your microphone. Please allow microphone access in your browser and try again. ' +
          'You can still continue to the next page without a recording.',
        'warn'
      )
      console.error('getUserMedia failed:', err)
      this.resolveSettled()
      return
    }

    this.chunks = []
    this.mimeType = pickMimeType()
    const filename = `${this.name || 'recording'}-${Date.now()}.${extensionFor(this.mimeType)}`

    try {
      this.recorder = new MediaRecorder(
        this.stream,
        this.mimeType
          ? { mimeType: this.mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
          : { audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
      )
    } catch (err) {
      this.releaseMicrophone()
      this.el.button.disabled = false
      this.setState('error')
      this.setStatus('This browser could not start a recording. Please try a different browser.', 'warn')
      console.error('MediaRecorder construction failed:', err)
      this.resolveSettled()
      return
    }

    // The recorder may pick a different type than we asked for.
    this.mimeType = this.recorder.mimeType || this.mimeType || 'audio/webm'
    this.upload = new ChunkedUpload(filename, this.mimeType, () => this.renderProgress())

    this.recorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return
      this.chunks.push(e.data)
      this.upload?.append(e.data)
      this.renderProgress()
    }
    this.recorder.onerror = (e) => {
      console.error('Recorder error:', e)
      // Whatever was captured before the error is still queued for upload, so
      // finish rather than discard it.
      void this.stop('Recording stopped unexpectedly. Saving what was captured…')
    }

    this.recorder.start(CHUNK_MS)
    this.startedAt = Date.now()
    this.el.button.disabled = false
    this.el.button.textContent = 'Stop recording'
    this.el.indicator.classList.remove('hidden')
    this.el.indicator.classList.add('inline')
    this.el.progressWrap.classList.remove('hidden')
    this.el.rescue.classList.add('hidden')
    this.setStatus('Recording. Your audio is being saved as you speak — there is no time limit.')
    this.setState('recording')

    this.tick = setInterval(() => this.renderElapsed(), 250)
    this.renderElapsed()
  }

  private resolveSettled: () => void = () => {}

  private renderElapsed() {
    this.el.elapsed.textContent = formatDuration(Date.now() - this.startedAt)
  }

  private renderProgress() {
    const upload = this.upload
    if (!upload) return
    if (upload.error) {
      this.el.progressBar.style.width = '100%'
      this.el.progressBar.classList.add('bg-accent-primary')
      return
    }
    const pct = upload.totalBytes === 0 ? 0 : (upload.uploadedBytes / upload.totalBytes) * 100
    this.el.progressBar.style.width = `${Math.min(100, Math.round(pct))}%`
    if (this.state === 'recording') {
      const saved = formatDuration(
        upload.totalBytes === 0
          ? 0
          : (upload.uploadedBytes / upload.totalBytes) * (Date.now() - this.startedAt)
      )
      this.setStatus(`Recording — saved through ${saved}. There is no time limit.`)
    }
  }

  /**
   * Stops the recorder and resolves once it has handed over its final chunk.
   *
   * Resolves rather than rejects on every path -- a recorder that fails to stop
   * cleanly must still leave the user able to save and submit.
   */
  private waitForRecorderToStop(): Promise<void> {
    const recorder = this.recorder
    // stop() throws if the recorder is already inactive, so never call it blind.
    if (!recorder || recorder.state === 'inactive') return Promise.resolve()

    return new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve()
      }
      // Don't hang the page if the browser never delivers `onstop`.
      const timer = setTimeout(() => {
        console.warn('Recorder did not report stopping; saving what we have.')
        finish()
      }, 5000)

      recorder.onstop = finish
      try {
        recorder.stop()
      } catch (err) {
        console.error('Error stopping recorder:', err)
        finish()
      }
    })
  }

  private releaseMicrophone() {
    // Without this the browser keeps showing the "recording" indicator.
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
  }

  private async stop(message?: string) {
    if (this.state !== 'recording') return
    this.setState('finishing')
    this.el.button.disabled = true
    this.el.button.textContent = 'Record Audio'
    if (this.tick) {
      clearInterval(this.tick)
      this.tick = null
    }
    this.el.indicator.classList.add('hidden')
    this.el.indicator.classList.remove('inline')
    this.setStatus(message ?? 'Finishing up — saving the last few seconds…')

    // Everything recorded since the last timeslice is delivered in one final
    // `dataavailable` event, which the browser fires *asynchronously* after
    // stop() returns and before `onstop`. Waiting for `onstop` is what keeps
    // that tail: building the blob or finalizing the upload any earlier drops
    // whatever the user said after the last automatic save.
    await this.waitForRecorderToStop()
    this.releaseMicrophone()

    // Let the user play back what they just recorded immediately, straight from
    // the browser rather than waiting on a round trip.
    const blob = new Blob(this.chunks, { type: this.mimeType })
    const localURL = URL.createObjectURL(blob)
    this.el.player.src = localURL
    this.el.player.load()

    try {
      const fileURL = await this.upload!.finalize()
      this.el.input.value = fileURL
      this.setState('ready')
      this.setStatus('Recording saved. You can play it back, or continue to the next page.', 'good')
      this.el.progressBar.style.width = '100%'
    } catch (err) {
      console.error('Failed to save recording:', err)
      this.setState('error')
      // The recording still exists in the browser. Offer it as a download so a
      // failed upload never means the user loses what they recorded.
      this.el.rescue.href = localURL
      this.el.rescue.download = `recording.${extensionFor(this.mimeType)}`
      this.el.rescue.classList.remove('hidden')
      this.setStatus(
        'We could not save your recording to the server. Your recording is safe in this browser — ' +
          'download a copy with the link below, then try recording again or continue and let your ' +
          'clinician know.',
        'warn'
      )
    } finally {
      this.el.button.disabled = false
      this.resolveSettled()
    }
  }
}

const controllers = new Map<HTMLElement, AudioRecorderController>()

/** Attaches controllers to any recorder markup that does not have one yet. */
export function attachAudioRecorders(scope: ParentNode = document) {
  scope.querySelectorAll<HTMLElement>('.audio-recorder').forEach((root) => {
    if (controllers.has(root)) return
    // Drop controllers whose markup has been replaced by a re-render.
    for (const [el] of controllers) {
      if (!el.isConnected) controllers.delete(el)
    }
    controllers.set(root, new AudioRecorderController(root))
  })
}

function liveControllers(): AudioRecorderController[] {
  return [...controllers.entries()].filter(([el]) => el.isConnected).map(([, c]) => c)
}

/** True if any recorder is mid-recording or still saving. */
export function anyAudioRecorderBusy(): boolean {
  return liveControllers().some((c) => c.busy)
}

/** True if a recording is currently being captured. */
export function anyAudioRecording(): boolean {
  return liveControllers().some((c) => c.state === 'recording')
}

/** Waits for every recorder to finish saving. */
export async function waitForAudioRecorders(): Promise<void> {
  await Promise.all(liveControllers().map((c) => c.whenSettled()))
}

/**
 * Watches for recorder markup appearing anywhere in the page and wires it up.
 *
 * Templates are injected with v-html from several different views, so an
 * observer keeps every one of them working without each view having to
 * remember to attach behaviour after it renders.
 */
export function observeAudioRecorders() {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
  attachAudioRecorders()
  new MutationObserver(() => attachAudioRecorders()).observe(document.body, {
    childList: true,
    subtree: true
  })

  // A recording in progress has not been fully uploaded yet; warn before the
  // page goes away.
  window.addEventListener('beforeunload', (e) => {
    if (!anyAudioRecorderBusy()) return
    e.preventDefault()
  })
}
